/** Bookmark folders: bar folder chip (dropdown), folder picker, and the star popover (name + folder, like Chrome). */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Folder, FolderPlus, ChevronDown, Trash2 } from "lucide-react";
import api from "@/lib/api";

export const BAR = ""; // folder "" = directly on the bookmark bar

const inp = "w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-[#1a3fd1] dark:border-white/15 dark:bg-white/5 dark:text-white";

/** <select> of existing folders + "New folder…" which turns into a text box. */
export const FolderSelect = ({ value, folders, onChange, testid }) => {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  if (creating) {
    const create = async () => { const n = name.trim(); if (!n) return; await api.post("/browser/folders", { name: n }); onChange(n, true); setCreating(false); setName(""); };
    return (
      <div className="flex gap-1">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }} placeholder="Folder name, e.g. Suppliers" className={inp} data-testid={`${testid}-new-name`} />
        <button type="button" onClick={create} className="shrink-0 rounded-md bg-[#1a3fd1] px-2 text-[11px] font-semibold text-white" data-testid={`${testid}-new-save`}>Create</button>
        <button type="button" onClick={() => setCreating(false)} className="shrink-0 rounded-md border border-gray-300 px-2 text-[11px] text-gray-700 dark:border-white/15 dark:text-white/70">Cancel</button>
      </div>
    );
  }
  return (
    <select value={value || BAR} data-testid={testid} className={inp}
      onChange={(e) => { if (e.target.value === "__new__") setCreating(true); else onChange(e.target.value); }}>
      <option value={BAR}>Bookmark bar</option>
      {folders.map((f) => <option key={f} value={f}>{f}</option>)}
      <option value="__new__">+ New folder…</option>
    </select>
  );
};

/** Folder on the bookmark bar — click to drop down its bookmarks. */
export const FolderChip = ({ name, items, onOpen, testid }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open]);
  const toggle = (e) => { const r = e.currentTarget.getBoundingClientRect(); setPos({ left: r.left, top: r.bottom + 4 }); setOpen((v) => !v); };
  return (
    <div ref={ref} className="relative shrink-0">
      <button type="button" onClick={toggle} data-testid={testid}
        className={`flex max-w-[10rem] items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-white/70 hover:bg-white/10 hover:text-white ${open ? "bg-white/10 text-white" : ""}`}>
        <Folder className="h-3.5 w-3.5 fill-current" /><span className="truncate">{name}</span><ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div style={{ position: "fixed", left: pos.left, top: pos.top }} className="z-[70] w-64 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-2xl dark:border-white/15 dark:bg-[#1f2126]" data-testid={`${testid}-menu`}>
          {items.length === 0 && <p className="px-3 py-2 font-mono text-[10px] text-gray-500 dark:text-white/50">Empty folder</p>}
          {items.map((b) => (
            <button key={b.bookmark_id} type="button" onClick={() => { onOpen(b.url); setOpen(false); }} title={b.url} data-testid={`browser-bar-${b.bookmark_id}`}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-800 hover:bg-gray-100 dark:text-white/85 dark:hover:bg-white/10">
              <img alt="" className="h-3.5 w-3.5 rounded-sm" src={b.favicon} /><span className="truncate">{b.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Popover under the ★: edit name + folder of the just-added bookmark, or remove it. */
export const BookmarkPopover = ({ bookmark, folders, onClose, onChanged }) => {
  const [title, setTitle] = useState(bookmark.title || "");
  const [folder, setFolder] = useState(bookmark.folder || BAR);
  const save = async (patch) => { await api.put(`/browser/bookmarks/${bookmark.bookmark_id}`, patch); onChanged(); };
  return (
    <>
      <div className="fixed inset-0 z-[80]" onClick={onClose} />
      <div className="absolute right-0 top-9 z-[90] w-72 rounded-xl border border-gray-200 bg-white p-3 text-gray-900 shadow-2xl dark:border-white/15 dark:bg-[#1f2126] dark:text-white" data-testid="browser-bookmark-popover">
        <p className="text-xs font-bold">Bookmark added</p>
        <p className="font-mono text-[10px] text-gray-500 dark:text-white/50">Shared with everyone who has the browser</p>
        <label className="mt-2 block text-[10px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/50">Name
          <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title.trim() && title !== bookmark.title && save({ title: title.trim() })} className={`${inp} mt-1`} data-testid="browser-bookmark-name" />
        </label>
        <div className="mt-2 text-[10px] font-medium uppercase tracking-widest text-gray-500 dark:text-white/50">Folder
          <div className="mt-1"><FolderSelect value={folder} folders={folders} testid="browser-bookmark-folder" onChange={async (f) => { setFolder(f); await save({ folder: f }); toast.success(f ? `Moved to ${f}` : "On the bookmark bar"); }} /></div>
        </div>
        <div className="mt-3 flex justify-between">
          <button type="button" onClick={async () => { await api.delete(`/browser/bookmarks/${bookmark.bookmark_id}`); onChanged(); onClose(); toast.success("Bookmark removed"); }} data-testid="browser-bookmark-remove"
            className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:underline dark:text-red-300"><Trash2 className="h-3 w-3" /> Remove</button>
          <button type="button" onClick={onClose} data-testid="browser-bookmark-done" className="rounded-md bg-[#1a3fd1] px-3 py-1 text-[11px] font-semibold text-white">Done</button>
        </div>
      </div>
    </>
  );
};

/** Folder header row used by the Bookmarks panel: rename inline, delete (bookmarks go back on the bar). */
export const FolderHeader = ({ name, count, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(name);
  const rename = async (e) => { e.preventDefault(); const n = val.trim(); if (n && n !== name) { await api.put(`/browser/folders/${encodeURIComponent(name)}`, { name: n }); onChanged(); toast.success(`Renamed to ${n}`); } setEditing(false); };
  return (
    <div className="mt-3 flex items-center gap-2 border-b border-gray-200 pb-1 dark:border-white/10" data-testid={`browser-folder-${name}`}>
      <Folder className="h-3.5 w-3.5 fill-[#f5c451] text-[#f5c451]" />
      {editing ? (
        <form onSubmit={rename} className="flex flex-1 gap-1"><input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={rename} className={inp} data-testid={`browser-folder-rename-${name}`} /></form>
      ) : (
        <button type="button" onClick={() => setEditing(true)} title="Rename folder" className="flex-1 text-left text-xs font-bold hover:underline" data-testid={`browser-folder-name-${name}`}>{name} <span className="font-mono text-[10px] font-normal text-gray-500 dark:text-white/50">· {count}</span></button>
      )}
      <button type="button" aria-label="Delete folder" title="Delete folder (its bookmarks move back to the bar)" data-testid={`browser-folder-del-${name}`}
        onClick={async () => { if (!window.confirm(`Delete folder "${name}"? Its bookmarks move back onto the bookmark bar.`)) return; await api.delete(`/browser/folders/${encodeURIComponent(name)}`); onChanged(); }}
        className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
};

export const NewFolderButton = ({ onChanged }) => (
  <button type="button" data-testid="browser-new-folder"
    onClick={async () => { const n = window.prompt("New folder name (e.g. Suppliers, Banks, Permits)"); if (n?.trim()) { await api.post("/browser/folders", { name: n.trim() }); onChanged(); } }}
    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] font-semibold text-gray-700 hover:border-[#1a3fd1] dark:border-white/15 dark:text-white/70 dark:hover:text-white"><FolderPlus className="h-3 w-3" /> New folder</button>
);
